import { FieldError, FieldsGroup } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC } from "react";
import { useEffect, useRef } from "react";
import { Dropdown } from "../../../../components/dropdown/dropdown";
import { Input } from "../../../../components/input/input";
import { Label } from "../../../../components/label/label";
import { Toggle } from "../../../../components/toggle/toggle";
import { currencySymbol } from "../../../../helpers/constants";
import type { FormSectionType } from "../../obPaymentPageForm";
import styles from "./amountSection.module.scss";

export const AmountSection: FC<FormSectionType> = observer(({ form, store }) => {
  const quantityInputRef = useRef<HTMLSpanElement>(null);
  const { switchedOAmount } = form.values;

  const handleChangeToggle = (value: boolean): void => {
    form.onChange("switchedOAmount", value);
    if (!value) {
      form.onChange("amount", "");
      form.onChange("paymentLinkDescription", "");
    }
  };

  useEffect(() => {
    quantityInputRef.current?.addEventListener("wheel", (e) => {
      e.preventDefault();
    });
  }, [quantityInputRef]);

  const withMargin = !!form.getValue("logo") && store.firstUse && !store.getValidationError("imageSize");

  return (
    <div className={classNames(styles.section, styles.withSeparator, withMargin && styles.withMargin)}>
      <div className={styles.amountSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionSubtitle}>How much to pay</span>
            <span className={styles.sectionDescription}>
              Include the amount customers need to pay you and an optional description
            </span>
          </div>
          <Toggle label="How much to pay" value={switchedOAmount} onChange={handleChangeToggle} />
        </div>
        {switchedOAmount && (
          <>
            <div className={styles.fieldRow}>
              <Label forId="amount" labelText="Amount" />
              <FieldsGroup className={styles.fieldGroup}>
                <Dropdown
                  name="currency"
                  className={styles.currencyField}
                  value={form.getValue("currency")}
                  hasError={!!form.getError("amount")}
                  options={[
                    { value: "GBP", label: currencySymbol.GBP ?? "GBP" },
                    { value: "USD", label: currencySymbol.USD ?? "USD" },
                    { value: "EUR", label: currencySymbol.EUR ?? "EUR" },
                  ]}
                  onChange={(value) => {
                    if (typeof value === "string") {
                      form.onChange("currency", value);
                    }
                  }}
                />
                <Input
                  className={styles.field}
                  ref={quantityInputRef}
                  type="number"
                  min={0}
                  hasError={!!form.getError("amount")}
                  placeholder="0.00"
                  id="amount"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    form.onChange("amount", e.target.value);
                    store.clearValidationError("amount");
                  }}
                  value={form.getValue("amount")}
                />
              </FieldsGroup>
              <FieldError errorText={form.getError("amount") || (store.getValidationError("amount") ?? "")} />
            </div>
            <div className={styles.fieldRow}>
              <Input
                label="What is this for?"
                className={styles.field}
                placeholder="Optional"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  form.onChange("paymentLinkDescription", e.target.value);
                }}
                hasError={!!form.getError("paymentLinkDescription")}
                error={form.getError("paymentLinkDescription")}
                value={form.getValue("paymentLinkDescription") ?? undefined}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});
